import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { isStandalone } from '../push/platform';

/**
 * Install-nudge state shared by the chrome banner, /install, and Settings.
 *
 * Chrome/Edge fire `beforeinstallprompt` once per page load; if the user dismisses the
 * native dialog the browser will not re-offer it on its own. We stash the event at module
 * scope so any button can re-fire it later. iOS never fires it — instructions-only there.
 *
 * The banner dismissal is a localStorage flag so it survives reloads; Settings can clear
 * it ("Show install banner again").
 */

const DISMISS_KEY = 'awwyeah:install-banner-dismissed';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    emit();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    emit();
  });
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export interface InstallPromptState {
  /** True when the browser handed us a native install prompt we can re-fire. */
  canPromptNatively: boolean;
  /** Fire the captured native prompt. Resolves to the user's choice, or null if unavailable. */
  promptNatively: () => Promise<'accepted' | 'dismissed' | null>;
  /** True when the chrome banner has been dismissed on this device. */
  bannerDismissed: boolean;
  dismissBanner: () => void;
  /** Settings: bring the banner back. */
  resetBannerDismissal: () => void;
  /** True when the app is already running standalone (nothing to nudge about). */
  installed: boolean;
}

export function useInstallPrompt(): InstallPromptState {
  const canPromptNatively = useSyncExternalStore(
    subscribe,
    () => deferredPrompt !== null,
    () => false,
  );
  const bannerDismissed = useSyncExternalStore(subscribe, readDismissed, () => true);

  // Storage events keep multiple tabs honest.
  useEffect(() => {
    const onStorage = (e: StorageEvent): void => {
      if (e.key === DISMISS_KEY) emit();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const promptNatively = useCallback(async (): Promise<'accepted' | 'dismissed' | null> => {
    const prompt = deferredPrompt;
    if (!prompt) return null;
    await prompt.prompt();
    const { outcome } = await prompt.userChoice;
    // A used prompt can't be fired twice; the browser may or may not offer a fresh one.
    deferredPrompt = null;
    emit();
    return outcome;
  }, []);

  const dismissBanner = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode — banner just reappears next load */
    }
    emit();
  }, []);

  const resetBannerDismissal = useCallback(() => {
    try {
      localStorage.removeItem(DISMISS_KEY);
    } catch {
      /* ignore */
    }
    emit();
  }, []);

  return {
    canPromptNatively,
    promptNatively,
    bannerDismissed,
    dismissBanner,
    resetBannerDismissal,
    installed: isStandalone(),
  };
}
